import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { usersAPI, departmentsAPI, companiesAPI, permissionsAPI, rolesAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import { AutoCloseAlert } from '../Common';

const CustomToolbar = ({ onAdd, hasCreatePermission, hasExportPermission }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    {hasExportPermission && (
      <GridToolbarExport 
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ 
          disableToolbarButton: false,
          fileName: 'Users_Management' // Custom filename for CSV export
        }}
      />
    )}
    {hasCreatePermission && (
      <Button startIcon={<AddIcon />} onClick={onAdd}>
        Add User
      </Button>
    )}
  </GridToolbarContainer>
);

const Users = () => {
  const { hasPermission } = usePermissions();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]); // State to store companies for dropdown
  const [filteredDepartments, setFilteredDepartments] = useState([]); // State to store filtered departments based on selected company
  const [roles, setRoles] = useState([]); // State to store roles for dropdown
  const [filteredRoles, setFilteredRoles] = useState([]); // State to store filtered roles based on selected company
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // Track if current user is superadmin
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    department_id: '',
    company_id: '', // Company ID for user association
    role_id: '', // Role ID for user role assignment
    is_active: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const canView = hasPermission('Users', 'view');
  const canCreate = hasPermission('Users', 'create');
  const canUpdate = hasPermission('Users', 'update');
  const canDelete = hasPermission('Users', 'delete');
  const canExport = hasPermission('Users', 'export');

  // Validation functions
  const validateForm = (data) => {
    const errors = {};

    // Name validation
    if (!data.name || !data.name.trim()) {
      errors.name = 'Name is required';
    } else if (data.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    } else if (data.name.trim().length > 100) {
      errors.name = 'Name must be less than 100 characters';
    } else if (!/^[a-zA-Z\s\-'\.]+$/.test(data.name.trim())) {
      errors.name = 'Name can only contain letters, spaces, hyphens, apostrophes, and periods';
    }

    // Email validation
    if (!data.email || !data.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errors.email = 'Please enter a valid email address';
    } else if (data.email.trim().length > 255) {
      errors.email = 'Email must be less than 255 characters';
    }

    // Password validation (only for new users or when password is provided)
    if (!editingUser && (!data.password || !data.password.trim())) {
      errors.password = 'Password is required for new users';
    } else if (data.password && data.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (data.password && data.password.length > 128) {
      errors.password = 'Password must be less than 128 characters';
    } else if (data.password && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Department validation
    if (!data.department_id) {
      errors.department_id = 'Department is required';
    }

    // Role validation
    if (!data.role_id) {
      errors.role_id = 'Role is required';
    }

    // Company validation (for superadmin)
    if (isSuperAdmin && !data.company_id) {
      errors.company_id = 'Company is required';
    }

    return errors;
  };

  const validateField = (fieldName, value) => {
    const errors = {};

    switch (fieldName) {
      case 'name':
        if (!value || !value.trim()) {
          errors.name = 'Name is required';
        } else if (value.trim().length < 2) {
          errors.name = 'Name must be at least 2 characters long';
        } else if (value.trim().length > 100) {
          errors.name = 'Name must be less than 100 characters';
        } else if (!/^[a-zA-Z\s\-'\.]+$/.test(value.trim())) {
          errors.name = 'Name can only contain letters, spaces, hyphens, apostrophes, and periods';
        }
        break;
      case 'email':
        if (!value || !value.trim()) {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors.email = 'Please enter a valid email address';
        } else if (value.trim().length > 255) {
          errors.email = 'Email must be less than 255 characters';
        }
        break;
      case 'password':
        if (!editingUser && (!value || !value.trim())) {
          errors.password = 'Password is required for new users';
        } else if (value && value.length < 8) {
          errors.password = 'Password must be at least 8 characters long';
        } else if (value && value.length > 128) {
          errors.password = 'Password must be less than 128 characters';
        } else if (value && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
        }
        break;
      case 'department_id':
        if (!value) {
          errors.department_id = 'Department is required';
        }
        break;
      case 'role_id':
        if (!value) {
          errors.role_id = 'Role is required';
        }
        break;
      case 'company_id':
        if (isSuperAdmin && !value) {
          errors.company_id = 'Company is required';
        }
        break;
      default:
        break;
    }

    return errors;
  };

  const handleFieldChange = (fieldName, value) => {
    // Update form data
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Clear previous error for this field
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });

    // Validate the field only if there are existing errors (to avoid showing errors while typing)
    if (Object.keys(formErrors).length > 0) {
      const fieldErrors = validateField(fieldName, value);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(prev => ({ ...prev, ...fieldErrors }));
      }
    }
  };

  // Debug logging
  console.log('👥 [Users] Permission check results:');
  console.log('👥 [Users] canView:', canView);
  console.log('👥 [Users] canCreate:', canCreate);
  console.log('👥 [Users] canUpdate:', canUpdate);
  console.log('👥 [Users] canDelete:', canDelete);
  console.log('👥 [Users] canExport:', canExport);

  // Add function to load companies for dropdown
  const loadCompanies = async () => {
    try {
      // Fetch all active companies for dropdown selection
      const response = await companiesAPI.getDropdown();
      const allCompanies = response.data.data || [];
      
      // Filter out merchant companies (assuming merchant companies have specific naming or type)
      const filteredCompanies = allCompanies.filter(company => {
        // Filter out companies that are merchant-related
        const companyName = company.company_name?.toLowerCase() || '';
        return !companyName.includes('merchant') && 
               !companyName.includes('customer') && 
               !companyName.includes('client');
      });
      
      setCompanies(filteredCompanies);
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to load companies for dropdown');
    }
  };

  // Add function to load roles for dropdown
  const loadRoles = async () => {
    try {
      // Fetch all active roles for dropdown selection
      const response = await rolesAPI.getDropdown();
      console.log('🔍 [Users] Roles API response:', response);
      const allRoles = response.data.data || [];
      
      // Filter out merchant roles
      const filteredRoles = allRoles.filter(role => {
        const roleName = role.role_name?.toLowerCase() || '';
        const departmentName = role.department_name?.toLowerCase() || '';
        
        // Filter out merchant-related roles and departments
        return !roleName.includes('merchant') && 
               !roleName.includes('customer') && 
               !roleName.includes('client') &&
               !departmentName.includes('merchant') && 
               !departmentName.includes('customer') && 
               !departmentName.includes('client');
      });
      
      console.log('🔍 [Users] Filtered roles data loaded:', filteredRoles);
      
      // Debug each role's structure
      filteredRoles.forEach((role, index) => {
        console.log(`🔍 [Users] Role ${index}:`, {
          id: role.id,
          role_name: role.role_name,
          department_id: role.department_id,
          department_name: role.department_name,
          company_id: role.company_id,
          hasDepartmentId: 'department_id' in role,
          departmentIdType: typeof role.department_id
        });
      });
      
      setRoles(filteredRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Failed to load roles for dropdown');
    }
  };

  // Add function to check if current user is superadmin
  const checkSuperAdminStatus = async () => {
    try {
      // Check if current user has Super Admin role to enable cross-company user management
      const response = await permissionsAPI.checkSuperAdmin();
      setIsSuperAdmin(response.data.is_superadmin || false);
    } catch (error) {
      console.error('Error checking superadmin status:', error);
      setIsSuperAdmin(false);
    }
  };

  useEffect(() => {
    console.log('🔄 [FRONTEND_USEEFFECT] Pagination model changed, triggering data reload');
    console.log('🔄 [FRONTEND_USEEFFECT] New pagination model:', paginationModel);
    loadUsers();
    loadDepartments();
    loadCompanies(); // Load companies for dropdown
    loadRoles(); // Load roles for dropdown
    checkSuperAdminStatus(); // Check if current user is superadmin
  }, [paginationModel]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('🔍 [FRONTEND_LOAD_USERS] Starting to load users');
      console.log('🔍 [FRONTEND_LOAD_USERS] Pagination model:', paginationModel);
      
      const response = await usersAPI.getAll({
        page: paginationModel.page + 1, // Backend expects 1-based page numbers
        per_page: paginationModel.pageSize,
      });
      
      console.log('🔍 [FRONTEND_LOAD_USERS] API response received:', response);
      console.log('🔍 [FRONTEND_LOAD_USERS] Users data:', response.data.data);
      console.log('🔍 [FRONTEND_LOAD_USERS] Total users:', response.data.total);
      
      setUsers(response.data.data || []);
      setRowCount(response.data.total || 0);
      
      console.log('✅ [FRONTEND_LOAD_USERS] Users loaded successfully');
    } catch (error) {
      console.error('❌ [FRONTEND_LOAD_USERS] Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll();
      const allDepartments = response.data.data || [];
      
      // Filter out merchant departments
      const filteredDepartments = allDepartments.filter(dept => {
        const departmentName = dept.department_name?.toLowerCase() || '';
        const companyName = dept.company_name?.toLowerCase() || '';
        
        // Filter out merchant-related departments and companies
        return !departmentName.includes('merchant') && 
               !departmentName.includes('customer') && 
               !departmentName.includes('client') &&
               !companyName.includes('merchant') && 
               !companyName.includes('customer') && 
               !companyName.includes('client');
      });
      
      setDepartments(filteredDepartments);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    setViewMode(false);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      department_id: '',
      company_id: isSuperAdmin ? '' : (currentUser?.company_id || ''), // Set current user's company for non-superadmin
      role_id: '', // Reset role_id for new user
      is_active: true,
    });
    
    console.log('🔍 [Users] handleAdd - Initial state:');
    console.log('🔍 [Users] - isSuperAdmin:', isSuperAdmin);
    console.log('🔍 [Users] - currentUser:', currentUser);
    console.log('🔍 [Users] - departments:', departments);
    console.log('🔍 [Users] - roles:', roles);
    
    // For non-superadmin users, automatically load departments from their company
    if (!isSuperAdmin && currentUser?.company_id) {
      const currentUserCompany = currentUser.company_id;
      // Filter departments to only show those belonging to the current user's company
      const filtered = departments.filter(dept => dept.company_id === currentUserCompany);
      setFilteredDepartments(filtered);
      // Clear roles initially - will be populated when department is selected
      setFilteredRoles([]);
      console.log('🔍 [Users] - Filtered departments for current user company:', filtered);
    } else {
      // For superadmin users, clear all filters initially
      setFilteredDepartments([]);
      setFilteredRoles([]);
      console.log('🔍 [Users] - Superadmin mode, clearing all filters');
    }
    setDialogOpen(true);
  };

  const handleView = (user) => {
    setEditingUser(user);
    setViewMode(true);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      department_id: user.department_id || '',
      company_id: isSuperAdmin ? (user.company_id || '') : (currentUser?.company_id || ''), // Set appropriate company
      role_id: user.role_id || '', // Set role_id for viewing
      is_active: user.is_active,
    });
    
    // Filter departments based on user's company
    const companyId = isSuperAdmin ? user.company_id : currentUser?.company_id;
    if (companyId) {
      const filtered = departments.filter(dept => dept.company_id === companyId);
      setFilteredDepartments(filtered);
      
      // Filter roles based on user's department
      if (user.department_id) {
        const filteredRoles = roles.filter(role => role.department_id === user.department_id);
        setFilteredRoles(filteredRoles);
      } else {
        setFilteredRoles([]);
      }
    } else {
      setFilteredDepartments([]);
      setFilteredRoles([]);
    }
    setDialogOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setViewMode(false);
    setError('');
    setFormErrors({});
    setIsSubmitting(false);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      department_id: user.department_id || '',
      company_id: isSuperAdmin ? (user.company_id || '') : (currentUser?.company_id || ''), // Set appropriate company
      role_id: user.role_id || '', // Set role_id for editing
      is_active: user.is_active,
    });
    
    // Filter departments based on user's company
    const companyId = isSuperAdmin ? user.company_id : currentUser?.company_id;
    if (companyId) {
      const filtered = departments.filter(dept => dept.company_id === companyId);
      setFilteredDepartments(filtered);
      
      // Filter roles based on user's department
      if (user.department_id) {
        const filteredRoles = roles.filter(role => role.department_id === user.department_id);
        setFilteredRoles(filteredRoles);
      } else {
        setFilteredRoles([]);
      }
    } else {
      setFilteredDepartments([]);
      setFilteredRoles([]);
    }
    setDialogOpen(true);
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      try {
        const response = await usersAPI.delete(userToDelete.id);
        setDeleteDialogOpen(false);
        setUserToDelete(null);
        
        // Show success message based on response
        if (response.data?.user_remains_active) {
          // Dual role user - only admin roles were deactivated
          const deletedRoles = response.data.deleted_roles || [];
          setSuccess(`Admin roles deactivated for "${userToDelete.name}". User remains active with merchant roles. Deactivated roles: ${deletedRoles.join(', ')}`);
        } else {
          // Pure admin user - entire user was deleted
          setSuccess('User deleted successfully');
        }
        
        // Check if we need to go back to previous page if current page becomes empty
        const currentPageStartIndex = paginationModel.page * paginationModel.pageSize;
        const totalItemsAfterDelete = rowCount - 1;
        
        if (currentPageStartIndex >= totalItemsAfterDelete && paginationModel.page > 0) {
          setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
        } else {
          loadUsers();
        }
      } catch (error) {
        setError('Failed to delete user');
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    // Add comprehensive logging for user creation/update
    console.log('🔧 [FRONTEND_USER_SUBMIT] Starting user submit process');
    console.log('🔧 [FRONTEND_USER_SUBMIT] Form data:', formData);
    console.log('🔧 [FRONTEND_USER_SUBMIT] Editing user:', editingUser);

    // Validate the entire form
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      console.log('❌ [FRONTEND_USER_SUBMIT] Validation failed:', validationErrors);
      console.log('❌ [FRONTEND_USER_SUBMIT] Number of validation errors:', Object.keys(validationErrors).length);
      console.log('❌ [FRONTEND_USER_SUBMIT] Error fields:', Object.keys(validationErrors));
      
      // Set all validation errors at once
      setFormErrors(validationErrors);
      setIsSubmitting(false);
      
      // Scroll to the first error field
      setTimeout(() => {
        const firstErrorField = document.querySelector('.MuiTextField-root .Mui-error');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      return;
    }

    // Validate that the selected role is not a merchant role
    const selectedRole = roles.find(role => role.id === parseInt(formData.role_id));
    if (selectedRole) {
      const roleName = selectedRole.role_name?.toLowerCase() || '';
      const departmentName = selectedRole.department_name?.toLowerCase() || '';
      
      if (roleName.includes('merchant') || 
          roleName.includes('customer') || 
          roleName.includes('client') ||
          departmentName.includes('merchant') || 
          departmentName.includes('customer') || 
          departmentName.includes('client')) {
        console.log('❌ [FRONTEND_USER_SUBMIT] Validation failed - Cannot assign merchant role');
        setError('Cannot assign merchant roles in the User Management section. Use Merchant Management instead.');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      if (editingUser) {
        console.log('🔧 [FRONTEND_USER_SUBMIT] Updating existing user:', editingUser.id);
        await usersAPI.update(editingUser.id, formData);
        setSuccess(`User updated successfully. Status: ${formData.is_active ? 'Active' : 'Inactive'}`);
        setDialogOpen(false);
        setViewMode(false);
        setFormErrors({});
        console.log('🔧 [FRONTEND_USER_SUBMIT] User updated, reloading users list');
        loadUsers(); // Reload current page
      } else {
        console.log('🔧 [FRONTEND_USER_SUBMIT] Creating new user');
        const response = await usersAPI.create(formData);
        console.log('✅ [FRONTEND_USER_SUBMIT] User created successfully:', response.data);
        setSuccess(`User created successfully. Status: ${formData.is_active ? 'Active' : 'Inactive'}`);
        setDialogOpen(false);
        setViewMode(false);
        setFormErrors({});
        
        // For new users, go to first page to see the new item
        console.log('🔧 [FRONTEND_USER_SUBMIT] Setting pagination to page 0 to show new user');
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      console.log('❌ [FRONTEND_USER_SUBMIT] Error occurred:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Operation failed';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };


  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'email', headerName: 'Email', width: 250 },
    {
      field: 'department_name',
      headerName: 'Department',
      width: 150,
      renderCell: (params) => (
        <Tooltip 
          title={params.row.tooltip_data?.department_tooltip || params.value || 'No Department'} 
          arrow
          placement="top"
        >
          <Chip
            label={params.value || 'No Department'}
            size="small"
            variant="outlined"
            color={params.row.is_dual_role ? 'primary' : 'default'}
          />
        </Tooltip>
      ),
    },
    {
      field: 'role_name',
      headerName: 'Role',
      width: 150,
      renderCell: (params) => (
        <Tooltip 
          title={params.row.tooltip_data?.role_tooltip || params.value || 'No Role'} 
          arrow
          placement="top"
        >
          <Chip
            label={params.value || 'No Role'}
            size="small"
            variant="outlined"
            color={params.row.is_dual_role ? 'primary' : 'secondary'}
          />
        </Tooltip>
      ),
    },
    {
      field: 'company_name',
      headerName: 'Company',
      width: 200,
      renderCell: (params) => (
        <Chip 
          label={params.value}
          variant="outlined"
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'string',
      valueGetter: (params) => {
        // Return "Active" or "Inactive" text for filtering instead of boolean
        return params.value ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Use the raw row data to get the actual boolean value
        const isActive = params.row.is_active;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'string',
      valueGetter: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return 'N/A';
        
        // Format date with leading zeros to show DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
      },
      sortComparator: (v1, v2) => {
        if (v1 === 'N/A' && v2 === 'N/A') return 0;
        if (v1 === 'N/A') return -1;
        if (v2 === 'N/A') return 1;
        
        // Convert DD/MM/YYYY back to Date objects for comparison
        const [day1, month1, year1] = v1.split('/').map(Number);
        const [day2, month2, year2] = v2.split('/').map(Number);
        
        const date1 = new Date(year1, month1 - 1, day1);
        const date2 = new Date(year2, month2 - 1, day2);
        
        return date1.getTime() - date2.getTime();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => {
        const actions = [];
        
        if (canView) {
          actions.push(
            <Tooltip title="View User Details" arrow>
            <GridActionsCellItem
              icon={<ViewIcon />}
              label="View"
              onClick={() => handleView(params.row)}
            />
            </Tooltip>
          );
        }
        
        if (canUpdate) {
          actions.push(
            <Tooltip title="Edit User" arrow>
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(params.row)}
            />
            </Tooltip>
          );
        }
        
        if (canDelete) {
          actions.push(
            <Tooltip title="Delete User" arrow>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleDelete(params.row)}
            />
            </Tooltip>
          );
        }
        
        return actions;
      },
    },
  ];

  // Add function to toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Users Management
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This section manages admin users only. Merchant users are managed in the Merchant Management section.
      </Typography>

      <AutoCloseAlert
        severity="error"
        message={error}
        show={!!error}
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />

      <AutoCloseAlert
        severity="success"
        message={success}
        show={!!success}
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />

      <Paper sx={{ height: 'auto', width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          checkboxSelection
          disableSelectionOnClick
          slots={{
            toolbar: () => <CustomToolbar onAdd={handleAdd} hasCreatePermission={canCreate} hasExportPermission={canExport} />
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setViewMode(false); setError(''); setFormErrors({}); setIsSubmitting(false); }} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit} noValidate>
          <DialogTitle>
            {viewMode ? 'View User' : (editingUser ? 'Edit User' : 'Add New User')}
          </DialogTitle>
          <DialogContent>
            
            
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              disabled={viewMode}
              error={!!formErrors.name}
              helperText={formErrors.name || 'Enter the user\'s full name (2-100 characters)'}
              inputProps={{ maxLength: 100 }}
              sx={{ mb: 2 }}
              FormHelperTextProps={{
                sx: {
                  color: formErrors.name ? 'error.main' : 'text.secondary',
                  fontWeight: formErrors.name ? 500 : 'normal'
                }
              }}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled={viewMode}
              error={!!formErrors.email}
              helperText={formErrors.email || 'Enter a valid email address'}
              inputProps={{ maxLength: 255 }}
              sx={{ mb: 2 }}
              FormHelperTextProps={{
                sx: {
                  color: formErrors.email ? 'error.main' : 'text.secondary',
                  fontWeight: formErrors.email ? 500 : 'normal'
                }
              }}
            />
            {!viewMode && (
              <TextField
                margin="dense"
                label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={formData.password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password || (editingUser ? 'Leave blank to keep current password' : 'Password must be at least 8 characters with uppercase, lowercase, and number')}
                inputProps={{ maxLength: 128 }}
                sx={{ mb: 2 }}
                FormHelperTextProps={{
                  sx: {
                    color: formErrors.password ? 'error.main' : 'text.secondary',
                    fontWeight: formErrors.password ? 500 : 'normal'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {/* Only show company dropdown for superadmin users */}
            {isSuperAdmin && (
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formErrors.company_id}>
                <InputLabel id="company-select-label">
                  Company
                </InputLabel>
                <Select
                  labelId="company-select-label"
                  id="company-select"
                  value={formData.company_id}
                  onChange={(e) => {
                    handleFieldChange('company_id', e.target.value);
                    setFormData({ 
                      ...formData, 
                      company_id: e.target.value,
                      department_id: '', // Reset department when company changes
                      role_id: '' // Reset role when company changes
                    });
                    
                    // Filter departments based on selected company
                    if (e.target.value) {
                      const selectedCompanyId = parseInt(e.target.value);
                      const filtered = departments.filter(dept => dept.company_id === selectedCompanyId);
                      setFilteredDepartments(filtered);
                      console.log('🔍 [Users] Filtered departments for company:', filtered);
                    } else {
                      setFilteredDepartments([]);
                    }
                    
                    // Reset filtered roles since department is also reset
                    setFilteredRoles([]);
                  }}
                  label="Company"
                  disabled={viewMode}
                >
                  <MenuItem value="">
                    <em>Select Company</em>
                  </MenuItem>
                  {/* Render company options for dropdown selection */}
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.company_name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.company_id && (
                  <Box sx={{ 
                    color: 'error.main', 
                    fontSize: '0.75rem', 
                    mt: 0.5, 
                    ml: 1.75,
                    fontWeight: 500
                  }}>
                    {formErrors.company_id}
                  </Box>
                )}
              </FormControl>
            )}

            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formErrors.department_id}>
              <InputLabel id="department-select-label">Department</InputLabel>
              <Select
                labelId="department-select-label"
                id="department-select"
                value={formData.department_id}
                onChange={(e) => {
                  handleFieldChange('department_id', e.target.value);
                  setFormData({ 
                    ...formData, 
                    department_id: e.target.value,
                    role_id: '' // Reset role when department changes
                  });
                  
                  // Filter roles based on selected department
                  if (e.target.value) {
                    const selectedDepartment = parseInt(e.target.value);
                    console.log('🔍 [Users] Department selected:', selectedDepartment);
                    
                    // Filter roles by department_id only
                    const filteredRoles = roles.filter(role => {
                      if (!role.department_id) {
                        return false;
                      }
                      const roleDeptId = parseInt(role.department_id);
                      return roleDeptId === selectedDepartment;
                    });
                    
                    console.log('🔍 [Users] Filtered roles for department:', filteredRoles);
                    setFilteredRoles(filteredRoles);
                  } else {
                    // If no department selected, clear roles
                    setFilteredRoles([]);
                  }
                }}
                label="Department"
                disabled={viewMode || (!isSuperAdmin && !formData.company_id)}
              >
                <MenuItem value="">
                  <em>{!isSuperAdmin && !formData.company_id ? 'Please select a company first' : 'Select Department'}</em>
                </MenuItem>
                {/* Only show filtered departments based on selected company */}
                {filteredDepartments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Box flexGrow={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {dept.department_name}
                        </Typography>
                        {dept.company_name && (
                          <Typography variant="caption" color="text.secondary">
                            {dept.company_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {formErrors.department_id && (
                <Box sx={{ 
                  color: 'error.main', 
                  fontSize: '0.75rem', 
                  mt: 0.5, 
                  ml: 1.75,
                  fontWeight: 500
                }}>
                  {formErrors.department_id}
                </Box>
              )}
            </FormControl>

            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} error={!!formErrors.role_id}>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={formData.role_id}
                onChange={(e) => handleFieldChange('role_id', e.target.value)}
                label="Role"
                disabled={viewMode || !formData.department_id}
              >
                <MenuItem value="">
                  <em>{formData.department_id ? 'Select Role' : 'Please select a department first'}</em>
                </MenuItem>
                {/* Only show filtered roles based on selected department */}
                {filteredRoles.map((role) => (
                  <MenuItem key={`role-${role.id}-${role.department_id}`} value={role.id}>
                    {role.role_name} - {role.department_name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.role_id && (
                <Box sx={{ 
                  color: 'error.main', 
                  fontSize: '0.75rem', 
                  mt: 0.5, 
                  ml: 1.75,
                  fontWeight: 500
                }}>
                  {formErrors.role_id}
                </Box>
              )}
              {formData.department_id && filteredRoles.length === 0 && !formErrors.role_id && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                  No roles available for the selected department
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                Note: Merchant roles are not available in this section. Use Merchant Management for merchant users.
              </Typography>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active Status"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); setViewMode(false); setError(''); setFormErrors({}); setIsSubmitting(false); }}>
              {viewMode ? 'Close' : 'Cancel'}
            </Button>
            {!viewMode && (
              <Button 
                type="submit" 
                variant="contained"
                disabled={isSubmitting || Object.keys(formErrors).length > 0}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting 
                  ? (editingUser ? 'Updating...' : 'Creating...') 
                  : (editingUser ? 'Update' : 'Create')
                }
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete the user{' '}
            <strong>{userToDelete?.name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone. The user will be permanently removed from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 